/** Small shared UI pieces: icons, dropdown menu, modal dialog. */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const ICONS: Record<string, string> = {
  select: 'M5 3 L5 17 L9 13 L12 19 L14 18 L11 12 L16 12 Z',
  wire: 'M3 17 H9 V7 H17 M3 17 m-1.5 0 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 M17 7 m-1.5 0 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0',
  text: 'M4 5 H16 M10 5 V17 M7 17 H13',
  rotate: 'M15.5 7.5 A6.5 6.5 0 1 0 16.5 13 M15.5 3 V7.5 H11',
  mirror: 'M10 2 V18 M8 5 L3 15 H8 Z M12 5 L17 15 H12 Z',
  delete: 'M4 6 H16 M8 6 V4 H12 V6 M6 6 L7 17 H13 L14 6',
  undo: 'M7 5 L3 9 L7 13 M3 9 H12 A5 5 0 0 1 12 19 H9',
  redo: 'M13 5 L17 9 L13 13 M17 9 H8 A5 5 0 0 0 8 19 H11',
  fit: 'M3 7 V3 H7 M13 3 H17 V7 M17 13 V17 H13 M7 17 H3 V13',
  menu: 'M3 6 H17 M3 10 H17 M3 14 H17',
  plus: 'M10 4 V16 M4 10 H16',
  more: 'M5 10 h0.01 M10 10 h0.01 M15 10 h0.01',
  print: 'M6 8 V3 H14 V8 M6 14 H4 V8 H16 V14 H14 M6 12 H14 V17 H6 Z',
  info: 'M10 9 V14 M10 6 h0.01 M10 2 A8 8 0 1 0 10.01 2',
  search: 'M8.5 3 A5.5 5.5 0 1 0 8.51 3 M12.5 12.5 L17 17',
  room: 'M3 4 H17 V16 H3 Z M3 10 H9 M12 4 V8',
  ruler: 'M2 13 L13 2 L18 7 L7 18 Z M5 10 L7 12 M8 7 L10 9 M11 4 L13 6',
  mic: 'M10 2 A3 3 0 0 0 7 5 V10 A3 3 0 0 0 13 10 V5 A3 3 0 0 0 10 2 Z M4 9.5 A6 6 0 0 0 16 9.5 M10 15.5 V18 M7 18 H13',
  stop: 'M5.5 5.5 H14.5 V14.5 H5.5 Z',
  sliders: 'M4 5 H16 M4 10 H16 M4 15 H16 M7 3.5 V6.5 M13 8.5 V11.5 M9 13.5 V16.5',
  spark: 'M10 2.5 L11.7 8.3 L17.5 10 L11.7 11.7 L10 17.5 L8.3 11.7 L2.5 10 L8.3 8.3 Z',
  sheet: 'M5 2.5 H12 L15.5 6 V17.5 H5 Z M12 2.5 V6 H15.5',
  'side-fold': 'M3 3.5 H17 V16.5 H3 Z M13 3.5 V16.5 M7 7.5 L9.5 10 L7 12.5',
  'side-unfold': 'M3 3.5 H17 V16.5 H3 Z M13 3.5 V16.5 M9.5 7.5 L7 10 L9.5 12.5',
  'pal-fold': 'M3 3.5 H17 V16.5 H3 Z M7 3.5 V16.5 M13 7.5 L10.5 10 L13 12.5',
  'pal-unfold': 'M3 3.5 H17 V16.5 H3 Z M7 3.5 V16.5 M10.5 7.5 L13 10 L10.5 12.5',
  chevron: 'M6 8 L10 12 L14 8',
  close: 'M5.5 5.5 L14.5 14.5 M14.5 5.5 L5.5 14.5',
};

export function Icon({ name, size = 18 }: { name: keyof typeof ICONS | string; size?: number }) {
  return (
    <svg className="icon" viewBox="0 0 20 20" width={size} height={size} aria-hidden="true">
      <path
        d={ICONS[name] ?? ''}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconButton(props: {
  icon: string;
  label: string;
  shortcut?: string;
  onClick(): void;
  active?: boolean;
  disabled?: boolean;
  testId?: string;
}) {
  const title = props.shortcut ? `${props.label} (${props.shortcut})` : props.label;
  return (
    <button
      className={`icon-btn${props.active ? ' active' : ''}`}
      onClick={props.onClick}
      disabled={props.disabled}
      title={title}
      aria-label={props.label}
      aria-pressed={props.active ?? undefined}
      data-testid={props.testId}
    >
      <Icon name={props.icon} />
    </button>
  );
}

export interface MenuItem {
  label: string;
  /** Not called for an item with a submenu: that item opens its submenu instead. */
  onSelect?(): void;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  separatorBefore?: boolean;
  testId?: string;
  /** Items shown in a side list next to this one (on hover, click or →). */
  submenu?: MenuItem[];
}

/** Click-to-open dropdown menu with keyboard dismissal and one level of submenus. */
export function Menu({
  trigger,
  items,
  align = 'left',
  testId,
}: {
  trigger: ReactNode;
  items: MenuItem[];
  align?: 'left' | 'right';
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // The open submenu (index into items), and where it is drawn.
  const [sub, setSub] = useState<number | null>(null);
  const [subPos, setSubPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const subList = useRef<HTMLDivElement>(null);
  const subOwner = useRef<HTMLButtonElement | null>(null);
  const subTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const focusSub = useRef(false);
  const clearSubTimer = () => clearTimeout(subTimer.current);
  const openSub = (i: number, owner: HTMLButtonElement) => {
    clearSubTimer();
    subOwner.current = owner;
    setSub(i);
  };
  // With a submenu open, moving onto another item closes it (or opens that item's submenu)
  // after a short delay, so the pointer can cross a neighbouring item on its way into the
  // submenu. Entering the submenu cancels it.
  const changeSubSoon = (i: number | null, owner?: HTMLButtonElement) => {
    clearSubTimer();
    subTimer.current = setTimeout(() => {
      if (owner) subOwner.current = owner;
      setSub(i);
    }, 250);
  };
  const close = () => {
    clearSubTimer();
    setSub(null);
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const inside = (n: EventTarget | null) =>
      !!n && (ref.current?.contains(n as Node) || list.current?.contains(n as Node));
    const dismiss = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !inside(e.target)) close();
    };
    // The list is placed once when it opens: close it if the page under it moves.
    const moved = (e: Event) => {
      if (!inside(e.target)) close();
    };
    window.addEventListener('mousedown', dismiss);
    window.addEventListener('keydown', dismiss);
    window.addEventListener('resize', moved);
    window.addEventListener('scroll', moved, true);
    return () => {
      window.removeEventListener('mousedown', dismiss);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('resize', moved);
      window.removeEventListener('scroll', moved, true);
    };
  }, [open]);
  useEffect(() => clearSubTimer, []);
  // The list is drawn over the page (not inside the trigger's box, which may clip it, like the
  // scrolling sheet tab bar), below the trigger, or above it when there is no room below.
  useLayoutEffect(() => {
    if (!open || !ref.current || !list.current) return setPos(null);
    const t = ref.current.getBoundingClientRect();
    const l = list.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 4;
    const margin = 8;
    let top = t.bottom + gap;
    if (top + l.height > vh - margin && t.top - gap - l.height >= margin) top = t.top - gap - l.height;
    top = Math.max(margin, Math.min(top, vh - margin - l.height));
    const left = Math.max(
      margin,
      Math.min(align === 'right' ? t.right - l.width : t.left, vw - margin - l.width),
    );
    setPos({ top, left });
  }, [open, align]);
  // A submenu sits beside its item, on the right, or on the left when there is no room.
  useLayoutEffect(() => {
    if (sub === null || !pos || !subOwner.current || !subList.current || !list.current)
      return setSubPos(null);
    const o = subOwner.current.getBoundingClientRect();
    const p = list.current.getBoundingClientRect();
    const l = subList.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    let left = p.right - 2;
    if (left + l.width > vw - margin) left = Math.max(margin, p.left - l.width + 2);
    const top = Math.max(margin, Math.min(o.top - 5, vh - margin - l.height));
    setSubPos({ top, left });
  }, [sub, pos]);
  // Opened from the keyboard: focus its first item once it is placed (a hidden one cannot be).
  useEffect(() => {
    if (!subPos || !focusSub.current) return;
    focusSub.current = false;
    subList.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }, [subPos]);
  const row = (it: MenuItem, key: number, inSub: boolean) => {
    const hasSub = !inSub && !!it.submenu?.length;
    return (
      <div key={key}>
        {it.separatorBefore && <div className="menu-sep" />}
        <button
          role="menuitem"
          className={`${it.danger ? 'danger' : ''}${hasSub && sub === key ? ' sub-open' : ''}`}
          disabled={it.disabled}
          data-testid={it.testId}
          aria-haspopup={hasSub ? 'menu' : undefined}
          aria-expanded={hasSub ? sub === key : undefined}
          onMouseEnter={(e) => {
            if (inSub) return clearSubTimer();
            if (hasSub && (sub === null || sub === key)) openSub(key, e.currentTarget);
            else if (hasSub) changeSubSoon(key, e.currentTarget);
            else if (sub !== null) changeSubSoon(null);
          }}
          onKeyDown={(e) => {
            if (hasSub && e.key === 'ArrowRight') {
              e.preventDefault();
              focusSub.current = true;
              openSub(key, e.currentTarget);
            } else if (inSub && e.key === 'ArrowLeft') {
              e.preventDefault();
              setSub(null);
              subOwner.current?.focus();
            }
          }}
          onClick={(e) => {
            if (hasSub) {
              // Opens (never toggles): a pointer that hovered it has already opened it.
              if (e.detail === 0) focusSub.current = true;
              return openSub(key, e.currentTarget);
            }
            close();
            it.onSelect?.();
          }}
        >
          <span>{it.label}</span>
          {hasSub ? (
            <span className="menu-sub-arrow">
              <Icon name="chevron" size={14} />
            </span>
          ) : (
            it.shortcut && <kbd>{it.shortcut}</kbd>
          )}
        </button>
      </div>
    );
  };
  const subItems = sub !== null ? items[sub]?.submenu : undefined;
  return (
    <div className="menu" ref={ref}>
      <button
        className="menu-trigger"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          <div
            ref={list}
            className="menu-list"
            role="menu"
            style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
          >
            {items.map((it, i) => row(it, i, false))}
            {subItems && (
              // Inside the main list's element (so a press in it counts as inside the menu), but
              // fixed in place, so the list's scrolling does not clip it.
              <div
                ref={subList}
                className="menu-list menu-sublist"
                role="menu"
                aria-label={items[sub!]!.label}
                onMouseEnter={clearSubTimer}
                style={
                  subPos ? { top: subPos.top, left: subPos.left } : { top: 0, left: 0, visibility: 'hidden' }
                }
              >
                {subItems.map((it, i) => row(it, i, true))}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Dialog(props: {
  title: string;
  children: ReactNode;
  onClose(): void;
  actions?: ReactNode;
  wide?: boolean;
  /** The content draws its own header and close button (sign-in, account). */
  bare?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button.primary, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        props.onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}>
      <div
        className={`dialog${props.wide ? ' wide' : ''}${props.bare ? ' bare' : ''}${props.className ? ` ${props.className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        ref={ref}
      >
        {!props.bare && <h3>{props.title}</h3>}
        <div className="dialog-body">{props.children}</div>
        {props.actions && <div className="dialog-actions">{props.actions}</div>}
      </div>
    </div>
  );
}

export interface ConfirmRequest {
  title: string;
  body: string;
  confirm: string;
  danger?: boolean;
  resolve(ok: boolean): void;
}

/** Hook-style confirm dialog (window.confirm is not available in every desktop webview). */
export function useConfirm() {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const ask = (r: Omit<ConfirmRequest, 'resolve'>) =>
    new Promise<boolean>((resolve) => setReq({ ...r, resolve }));
  const node = req && (
    <Dialog
      title={req.title}
      onClose={() => {
        req.resolve(false);
        setReq(null);
      }}
      actions={
        <ConfirmActions
          confirm={req.confirm}
          danger={req.danger}
          onAnswer={(ok) => {
            req.resolve(ok);
            setReq(null);
          }}
        />
      }
    >
      <p>{req.body}</p>
    </Dialog>
  );
  return { ask, node };
}

let cancelLabel = 'Annuler';
export const setCancelLabel = (s: string) => (cancelLabel = s);

function ConfirmActions({
  confirm,
  danger,
  onAnswer,
}: {
  confirm: string;
  danger?: boolean;
  onAnswer(ok: boolean): void;
}) {
  return (
    <>
      <button onClick={() => onAnswer(false)}>{cancelLabel}</button>
      <button
        className={danger ? 'primary danger' : 'primary'}
        onClick={() => onAnswer(true)}
        data-testid="confirm-ok"
      >
        {confirm}
      </button>
    </>
  );
}
